import React, { useEffect } from 'react';
import LogoSpinner from './Components/Common/LogoSpinner';
import ErrorBoundary from './Components/Common/ErrorBoundary';

//importer Routes
import Route from "./Routes";

// Import Scss
import './assets/scss/themes.scss';

// Import header dropdown position fix
import './assets/css/header-dropdown-fix.css';

// Import header tooltip fix
import './assets/css/header-tooltip-fix.css';

// Import messages CSS
import './assets/css/messages.css';

//importer i18n
import './i18n';

// toasts
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Notification context
import { NotificationProvider } from './contexts/NotificationContext';

// Rocket animation context
import { RocketProvider } from './contexts/RocketContext';

// Global loading context
import { GlobalLoadingProvider } from './contexts/GlobalLoadingContext';

// Global keyboard shortcuts
import GlobalKeyboardShortcuts from './Components/Common/GlobalKeyboardShortcuts';
import WebSocketInitializer from './Components/Common/WebSocketInitializer';

// Activity tracking
import { initGlobalClickTracking } from './hooks/useActivityTracker';

// Image interceptor to prevent 404 errors from invalid image URLs
import { initializeImageInterceptor } from './helpers/imageInterceptor';

// Axios interceptors for token refresh
import { setupAxiosInterceptors, startTokenRefreshTimer, stopTokenRefreshTimer } from './helpers/axios-interceptor';

// WebSocket debugging utilities (development only)
if (typeof import.meta !== 'undefined' && import.meta.env?.DEV) {
  import('./utils/websocket-debug');
  import('./utils/websocket-test');
  import('./utils/errorLogger');
} else if (process.env.NODE_ENV === 'development') {
  // Fallback for non-Vite environments
  import('./utils/errorLogger');
}

function App() {
  const [appReady, setAppReady] = React.useState(false);
  
  useEffect(() => {
    // Set default document title
    document.title = 'MXR Space';
    
    // Apply saved dark mode setting
    const savedMode = localStorage.getItem('layout-mode');
    if (savedMode === 'dark') {
      document.documentElement.setAttribute('data-bs-theme', 'dark');
    } else if (savedMode === 'light') {
      document.documentElement.setAttribute('data-bs-theme', 'light');
    }
    
    // Mark app as ready and hide initial loader
    const timer = setTimeout(() => {
      setAppReady(true);
      // Hide the initial loader from index.html
      if (window.hideInitialLoader) {
        window.hideInitialLoader();
      }
    }, 0);
    
    return () => clearTimeout(timer);
  }, []);
  
  useEffect(() => {
    // Validate token on app start
    const validateTokenOnStart = () => {
      const authUser = sessionStorage.getItem('authUser') || localStorage.getItem('authUser');
      
      if (authUser) {
        try {
          const parsed = JSON.parse(authUser);
          const token = parsed.token || parsed.data?.token;
          
          if (token) {
            try {
              // Decode and check token expiration
              const tokenPayload = JSON.parse(atob(token.split('.')[1]));
              const expiresAt = tokenPayload.exp * 1000; // Convert to milliseconds
              const now = Date.now();
              
              // If token is already expired, clear auth and redirect
              if (expiresAt <= now) {
                console.log('Token expired on app start, redirecting to login');
                sessionStorage.removeItem('authUser');
                localStorage.removeItem('authUser');
                
                if (!window.location.pathname.includes('/login') && !window.location.pathname.includes('/public/keys')) {
                  window.location.href = '/login?session=expired';
                }
              }
            } catch (e) {
              console.error('Invalid token format:', e);
              // Invalid token, clear auth
              sessionStorage.removeItem('authUser');
              localStorage.removeItem('authUser');
              
              if (!window.location.pathname.includes('/login') && !window.location.pathname.includes('/public/keys')) {
                window.location.href = '/login?session=expired';
              }
            }
          }
        } catch (e) {
          console.error('Error parsing authUser on app start:', e);
        }
      }
    };
    
    // Validate token immediately on app start
    validateTokenOnStart();
    
    // Initialize axios interceptors for token refresh
    setupAxiosInterceptors();
    startTokenRefreshTimer();
    
    // Initialize global click tracking with cleanup
    const cleanupClickTracking = initGlobalClickTracking();
    
    // Initialize image interceptor to prevent 404 errors from invalid image URLs
    initializeImageInterceptor();
    
    // Clean up old calculation drafts from localStorage
    const cleanupOldDrafts = () => {
      const keys = Object.keys(localStorage);
      const now = Date.now();
      const SEVEN_DAYS = 7 * 24 * 60 * 60 * 1000;
      
      keys.forEach(key => {
        if (key.startsWith('calculation_draft_')) {
          try {
            const draft = JSON.parse(localStorage.getItem(key) || '{}');
            if (now - draft.timestamp > SEVEN_DAYS) {
              localStorage.removeItem(key);
              console.log(`Removed old draft: ${key}`);
            }
          } catch (e) {
            // Invalid JSON, remove it
            localStorage.removeItem(key);
          }
        }
      });
    };
    
    cleanupOldDrafts();
    
    // Handle authentication sync from parent window (for new tabs)
    const handleAuthSync = (event: MessageEvent) => {
      // Only accept messages from same origin
      if (event.origin !== window.location.origin) return;
      
      // Check if it's an auth sync message
      if (event.data?.type === 'AUTH_SYNC' && event.data?.authData) {
        // Check if we don't already have auth data
        if (!sessionStorage.getItem('authUser')) {
          sessionStorage.setItem('authUser', event.data.authData);
          // Reload to apply the authentication
          window.location.reload();
        }
      }
    };
    
    window.addEventListener('message', handleAuthSync);
    
    // Cleanup function
    return () => {
      if (cleanupClickTracking) {
        cleanupClickTracking();
      }
      window.removeEventListener('message', handleAuthSync);
      stopTokenRefreshTimer();
    };
  }, []);

  // Show loading state until app is ready
  if (!appReady) {
    return <LogoSpinner text="MXR Space wird geladen..." />;
  }
  
  return (
    <React.Fragment>
      <ErrorBoundary>
        <GlobalLoadingProvider>
          <RocketProvider>
            <NotificationProvider>
              <Route />
              <ToastContainer closeButton={false} />
              <GlobalKeyboardShortcuts />
              <WebSocketInitializer />
            </NotificationProvider>
          </RocketProvider>
        </GlobalLoadingProvider>
      </ErrorBoundary>
    </React.Fragment>
  );
}

export default App;
